import { logger } from '@/ui'
import { paymentMethodAuthorise } from '@jobilla/nexus-sdk'
import { getStripe } from './stripe'

export class PaymentAuthorisationError extends Error {
	constructor(
		public readonly reason: 'declined' | 'three_ds_failed' | 'stripe_unavailable' | 'unknown',
		cause?: unknown,
	) {
		super(`Payment authorisation failed: ${reason}`)
		this.name = 'PaymentAuthorisationError'
		if (cause) { this.cause = cause }
	}
}

/**
 * Authorise a saved payment method for `amountCents` against the billing profile
 * the campaign will be submitted against. The backend creates a manual-capture
 * PaymentIntent; if the card requires 3DS we complete the next action
 * client-side via Stripe.js, then return the final PaymentIntent ID for the
 * caller to forward to submit-for-review.
 *
 * `profileId` must match the one passed to submit-for-review; the backend
 * rejects a card not attached to that profile's Stripe customer.
 *
 * Throws `PaymentAuthorisationError` on any failure path.
 */
export async function authorisePaymentMethod(args: {
	organisationId: string
	campaignId: string
	paymentMethodId: string
	amountCents: number
	profileId: string | null
}): Promise<string> {
	const { organisationId, campaignId, paymentMethodId, amountCents, profileId } = args

	const idempotencyToken = crypto.randomUUID()

	const { data } = await paymentMethodAuthorise(
		organisationId,
		campaignId,
		paymentMethodId,
		{ amount: amountCents, idempotencyToken, profileId },
	)

	if (data.status === 'requires_capture' || data.status === 'succeeded') {
		return data.paymentIntentId
	}

	if (data.status === 'requires_action') {
		if (!data.clientSecret) {
			throw new PaymentAuthorisationError('three_ds_failed')
		}
		const stripe = await getStripe()
		if (!stripe) {
			throw new PaymentAuthorisationError('stripe_unavailable')
		}
		const { paymentIntent, error } = await stripe.handleNextAction({
			clientSecret: data.clientSecret,
		})
		if (error) {
			logger.warn('[authorisePaymentMethod] handleNextAction returned an error', {
				category: 'billing',
				data: { code: error.code, message: error.message, payment_intent_id: data.paymentIntentId },
			})
			throw new PaymentAuthorisationError('three_ds_failed', error)
		}
		if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
			return paymentIntent.id
		}
		throw new PaymentAuthorisationError('three_ds_failed')
	}

	if (data.status === 'requires_payment_method') {
		throw new PaymentAuthorisationError('declined')
	}

	throw new PaymentAuthorisationError('unknown')
}
